import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { VaCardsModule } from '@vendasta/uikit';
import { fromEvent as observableFromEvent, interval, merge as observableMerge, of as observableOf } from 'rxjs';
import { mapTo, startWith, switchMap } from 'rxjs/operators';
import { WordpressContentItem } from '../../core/wordpress-content.service';

@Component({
  standalone: true,
  imports: [CommonModule, MatButtonModule, VaCardsModule, GalaxyPipesModule],
  selector: 'app-carousel',
  styleUrls: ['./va-carousel.component.scss'],
  template: `
    <div class="carousel-container">
      <a
        *ngFor="let slide of slides; let i = index"
        class="carousel-slide-container"
        [ngStyle]="{ 'background-color': slide.bgColor, 'background-image': 'url(' + slide.bgImage + ')' }"
        [ngClass]="{ 'active-slide': isActiveSlideIndex(i) }"
        href="{{ slide.primaryButtonLink }}"
        target="_blank"
      >
        <div
          [ngClass]="newVersion ? 'slide-title-v2' : 'slide-title'"
          [innerHTML]="slide.titleHtml | iTrustThisHtml"
        ></div>
        <div
          [ngClass]="newVersion ? 'slide-desc-v2' : 'slide-desc'"
          [innerHTML]="slide.descTextHtml | iTrustThisHtml"
        ></div>
        <ng-container *ngIf="!newVersion">
          <div class="slide-ctas">
            <a
              mat-raised-button
              color="primary"
              (click)="slideClickedAtIndex.emit({ slide: slide, index: slideIndex })"
            >
              {{ slide.primaryButtonText }}
            </a>
          </div>
        </ng-container>
      </a>

      <ng-container *ngIf="slides.length > 1">
        <i class="material-icons prev circle-icon" (click)="incrementSlideIndexBy(-1)">navigate_before</i>
        <i class="material-icons next circle-icon" (click)="incrementSlideIndexBy(1)">navigate_next</i>

        <div class="slide-markers">
          <div
            class="slide-marker"
            *ngFor="let slide of slides; let i = index"
            [ngClass]="{ 'slide-marker-active': isActiveSlideIndex(i) }"
          ></div>
        </div>
      </ng-container>
    </div>
    <ng-container *ngIf="newVersion">
      <va-card-link-button
        text="COMMON.ACTION_LABELS.LEARN_MORE"
        navigationIconName="chevron_right"
        (click)="goToLink()"
      ></va-card-link-button>
    </ng-container>
  `,
})
export class VaCarouselComponent implements OnInit {
  @Input() slides: WordpressContentItem[];
  @Input() autoSlideInterval: number;
  @Input() newVersion: boolean;
  @Output() slideClickedAtIndex = new EventEmitter<{ slide: WordpressContentItem; index: number }>();

  slideIndex = 0;

  ngOnInit(): void {
    if (this.autoSlideInterval) {
      const carousel = document.getElementsByClassName('image-carousel')[0];
      const interval$ = interval(this.autoSlideInterval).pipe(mapTo(true));
      const pause$ = observableFromEvent(carousel, 'mouseenter').pipe(mapTo(observableOf(false)));
      const resume$ = observableFromEvent(carousel, 'mouseleave').pipe(mapTo(interval$));

      observableMerge(pause$, resume$)
        .pipe(
          startWith(interval$),
          switchMap((val) => val),
        )
        .subscribe((increment) => {
          if (increment) {
            this.incrementSlideIndexBy(1);
          }
        });
    }
  }

  isActiveSlideIndex(index: number): boolean {
    return index === this.slideIndex;
  }

  incrementSlideIndexBy(n: any): void {
    this.slideIndex = this.slideIndex + n;

    if (this.slideIndex === this.slides.length) {
      this.slideIndex = 0;
    }

    if (this.slideIndex < 0) {
      this.slideIndex = this.slides.length - 1;
    }
  }

  goToLink(): void {
    this.slideClickedAtIndex.emit({ slide: this.slides[this.slideIndex], index: this.slideIndex });
  }
}
